/**
 * The session token for a public (not signed-in) quiz attempt.
 *
 * WHY (code review 2026-09-12). The public quiz endpoints used to treat the
 * attemptId as the entitlement — and an attempt id is a Mongo ObjectId, which
 * sits in the URL and in server logs and increments predictably. Anyone holding
 * one could read another person's marks, replace their answers, or submit their
 * paper mid-exam. `/public/start` now returns an opaque token and every later
 * call has to present it.
 *
 * Kept in `sessionStorage`, keyed by attempt: it survives a reload of the exam
 * tab, which is what a student needs, and does not leak into another tab or
 * outlive the session. Every access is wrapped — sessionStorage throws in a
 * private window and in some embedded webviews, and a quiz must still run.
 *
 * @module utils/quizSession
 */

const key = (attemptId) => `quizSession:${attemptId}`;

/**
 * Keep the token minted for this attempt.
 * @param {string} attemptId
 * @param {string} token
 */
export const rememberQuizSession = (attemptId, token) => {
  if (!attemptId || !token) return;
  try {
    sessionStorage.setItem(key(attemptId), token);
  } catch {
    // Storage unavailable (private window, blocked site data). The in-memory
    // Redux copy still carries the attempt for this page's lifetime.
  }
};

/**
 * The token for this attempt, if we have it.
 * @param {string} attemptId
 * @returns {string|undefined}
 */
export const quizSessionToken = (attemptId) => {
  if (!attemptId) return undefined;
  try {
    return sessionStorage.getItem(key(attemptId)) || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Drop the token once the attempt is finished with.
 * @param {string} attemptId
 */
export const forgetQuizSession = (attemptId) => {
  if (!attemptId) return;
  try {
    sessionStorage.removeItem(key(attemptId));
  } catch {
    // nothing to do — see rememberQuizSession
  }
};

export default { rememberQuizSession, quizSessionToken, forgetQuizSession };
